/**
 * AdminAffiliateApplications — admin queue for approving/rejecting
 * affiliate / vendor / brokerage applications.
 * Route: /admin/affiliate-applications
 */
import { useEffect, useState } from "react";
import { Loader2, CheckCircle2, XCircle, ExternalLink, RefreshCw } from "lucide-react";
import { api } from "@/lib/api";
import { PageHeader, Card } from "@/components/ui/Primitives";
import { usePageTitle } from "@/lib/seo";
import { toast } from "sonner";

export default function AdminAffiliateApplications() {
  usePageTitle("Affiliate Applications — Central Rentals Admin");
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState("");

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/affiliate-applications");
      setRows(data?.applications || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't load applications.");
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const decide = async (id, verb) => {
    setBusyId(id);
    const reason = verb === "reject" ? window.prompt("Optional rejection note (won't be emailed):") ?? "" : "";
    try {
      const { data } = await api.post(`/admin/affiliate-applications/${id}/${verb}`, { reason });
      if (verb === "approve") toast.success(`Approved. Referral link: /a/${data.slug}`);
      else toast.success("Application rejected.");
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || `Couldn't ${verb}.`);
    } finally { setBusyId(""); }
  };

  return (
    <div className="max-w-6xl mx-auto px-5 py-8" data-testid="admin-affiliate-apps-page">
      <div className="flex items-start justify-between gap-4">
        <PageHeader
          title="Affiliate Applications"
          subtitle="Approve or reject people applying to the affiliate, vendor, or brokerage program."
          icon={CheckCircle2}
        />
        <button type="button" onClick={load} disabled={loading} data-testid="admin-apps-reload"
          className="inline-flex items-center gap-1 px-3 py-1.5 rounded-md border border-slate-200 hover:border-slate-300 text-xs font-semibold text-slate-700 disabled:opacity-60">
          <RefreshCw className={`w-3.5 h-3.5 ${loading ? "animate-spin" : ""}`}/> Reload
        </button>
      </div>

      {loading ? (
        <div className="mt-8 flex items-center text-slate-500" data-testid="admin-apps-loading">
          <Loader2 className="w-5 h-5 animate-spin mr-2"/> Loading queue…
        </div>
      ) : rows.length === 0 ? (
        <Card className="mt-8 p-8 text-center" data-testid="admin-apps-empty">
          <div className="text-sm text-slate-500">No applications yet.</div>
        </Card>
      ) : (
        <Card className="mt-6 p-0 overflow-x-auto">
          <table className="w-full text-sm" data-testid="admin-apps-table">
            <thead>
              <tr className="bg-slate-50 text-xs uppercase tracking-wider text-slate-600">
                <th className="text-left px-4 py-2.5 font-bold">Name</th>
                <th className="text-left px-4 py-2.5 font-bold">Kind</th>
                <th className="text-left px-4 py-2.5 font-bold">Email</th>
                <th className="text-left px-4 py-2.5 font-bold">Why</th>
                <th className="text-left px-4 py-2.5 font-bold">Status</th>
                <th className="px-4 py-2.5"></th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.id} data-testid={`admin-app-row-${r.id}`} className="border-t border-slate-100">
                  <td className="px-4 py-3 font-semibold text-slate-900 whitespace-nowrap">{r.name}<br/><span className="text-[11px] font-normal text-slate-500">{r.company || ""}</span></td>
                  <td className="px-4 py-3 text-xs uppercase tracking-wider font-bold text-emerald-700">{r.kind}</td>
                  <td className="px-4 py-3 text-slate-700 whitespace-nowrap">{r.email}</td>
                  <td className="px-4 py-3 text-slate-600 max-w-md"><div className="line-clamp-2">{r.why}</div></td>
                  <td className="px-4 py-3">
                    <StatusPill status={r.status} />
                    {r.affiliate_slug && (
                      <a href={`/a/${r.affiliate_slug}`} target="_blank" rel="noopener noreferrer" className="ml-2 inline-flex items-center gap-1 text-[11px] font-semibold text-emerald-700 hover:text-emerald-900">
                        /a/{r.affiliate_slug} <ExternalLink className="w-3 h-3"/>
                      </a>
                    )}
                  </td>
                  <td className="px-4 py-3 whitespace-nowrap">
                    {r.status === "pending" && (
                      <div className="flex items-center gap-1">
                        <button disabled={busyId === r.id} onClick={() => decide(r.id, "approve")} data-testid={`admin-app-approve-${r.id}`} className="inline-flex items-center gap-1 px-2.5 py-1 rounded-md bg-emerald-600 hover:bg-emerald-700 text-white text-xs font-bold disabled:opacity-60">
                          <CheckCircle2 className="w-3.5 h-3.5"/> Approve
                        </button>
                        <button disabled={busyId === r.id} onClick={() => decide(r.id, "reject")} data-testid={`admin-app-reject-${r.id}`} className="inline-flex items-center gap-1 px-2.5 py-1 rounded-md border border-slate-200 hover:border-rose-400 text-slate-700 hover:text-rose-700 text-xs font-bold disabled:opacity-60">
                          <XCircle className="w-3.5 h-3.5"/> Reject
                        </button>
                      </div>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </Card>
      )}
    </div>
  );
}

function StatusPill({ status }) {
  const tone = status === "approved" ? "bg-emerald-100 text-emerald-800"
    : status === "rejected" ? "bg-rose-100 text-rose-800"
    : "bg-amber-100 text-amber-800";
  return <span className={`inline-block px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-widest ${tone}`}>{status}</span>;
}
